import { useEffect, useRef, useState } from "react";
import { EventLayout } from "@/components/EventLayout";
import { useEvent } from "@/context/EventContext";
import { API, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Eye, Upload, Trash2, Printer, QrCode, Copy } from "lucide-react";

const QUESTIONS = [
  { key: "plus_one", label: "Plus-ones", desc: "Let guests add the people joining them (within their allowance)." },
  { key: "meal", label: "Meal choice", desc: "Ask guests to pick a meal from your options." },
  { key: "dietary", label: "Dietary needs", desc: "A yes/no question; guests who answer Yes add details." },
  { key: "song", label: "Song request", desc: "A song they'd love to hear." },
  { key: "accessibility", label: "Accessibility needs", desc: "A yes/no question; guests who answer Yes add details." },
];

export default function Invitation() {
  const { eapi, event, features, eventId } = useEvent();
  const [form, setForm] = useState({ custom_title: "", message: "" });
  const [meta, setMeta] = useState({});
  const [preview, setPreview] = useState("");
  const [saving, setSaving] = useState(false);
  const [cfg, setCfg] = useState({ plus_one: true, meal: false, meal_options: [], dietary: true, song: false, accessibility: false });
  const [mealText, setMealText] = useState("");
  const [savingCfg, setSavingCfg] = useState(false);
  const [imgUploading, setImgUploading] = useState(false);
  const imgRef = useRef();

  useEffect(() => {
    eapi.get("/invitation").then(({ data }) => { setForm({ custom_title: data.custom_title || "", message: data.message || "" }); setMeta(data); });
    eapi.get("/rsvp-config").then(({ data }) => { setCfg(data); setMealText((data.meal_options || []).join(", ")); });
  }, []); // eslint-disable-line

  const refreshPreview = async () => {
    try { const { data } = await eapi.get("/invitation/preview"); setPreview(data.html); } catch { /* ignore */ }
  };

  const savePreview = async () => {
    setSaving(true);
    try {
      await eapi.put("/invitation", form);
      await refreshPreview();
      toast.success("Saved. Preview updated");
    } catch (e) { toast.error("Could not save the invitation."); }
    finally { setSaving(false); }
  };

  const uploadImage = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) { toast.error("Image must be 10 MB or smaller."); if (imgRef.current) imgRef.current.value = ""; return; }
    setImgUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await eapi.post("/invitation/image", fd);
      setMeta((m) => ({ ...m, image_url: data.image_url }));
      if (preview) await refreshPreview();
      toast.success("Header photo added");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setImgUploading(false); if (imgRef.current) imgRef.current.value = ""; }
  };

  const removeImage = async () => {
    try {
      await eapi.delete("/invitation/image");
      setMeta((m) => ({ ...m, image_url: "" }));
      if (preview) await refreshPreview();
      toast.success("Header photo removed");
    } catch { toast.error("Could not remove the photo."); }
  };

  const saveCfg = async () => {
    setSavingCfg(true);
    try {
      const payload = { ...cfg, meal_options: mealText.split(",").map((s) => s.trim()).filter(Boolean) };
      await eapi.put("/rsvp-config", payload);
      setCfg(payload);
      toast.success("RSVP questions saved");
    } catch (e) { toast.error("Could not save RSVP questions."); }
    finally { setSavingCfg(false); }
  };

  const invitesUnlocked = (features?.unlocked || []).includes("invites");
  const nameRsvpUrl = event?.public_slug ? `${window.location.origin}/r/${event.public_slug}` : "";
  const qrSrc = eventId ? `${API}/events/${eventId}/rsvp-qr.png?base=${encodeURIComponent(window.location.origin)}&token=${localStorage.getItem("token") || ""}` : "";
  const printQr = () => {
    const w = window.open("", "_blank");
    if (!w) { toast.error("Please allow pop-ups to print."); return; }
    w.document.write(`<html><head><title>RSVP QR</title></head><body style="text-align:center;font-family:Georgia,serif;padding:48px;color:#2C2A28;">
      <div style="letter-spacing:3px;font-size:12px;text-transform:uppercase;color:#8C9A86;">You're invited to</div>
      <h1 style="font-size:34px;margin:8px 0 4px;">${(event?.title) || "Our Celebration"}</h1>
      <p style="color:#6B6661;margin:0 0 24px;">Scan to find your name and RSVP</p>
      <img src="${qrSrc}" style="width:320px;height:320px;" />
      <p style="color:#A39E98;font-size:12px;margin-top:16px;">${nameRsvpUrl}</p>
    </body></html>`);
    w.document.close();
    setTimeout(() => w.print(), 600);
  };

  const detailLine = [meta.date, meta.venue_name, meta.venue_address].filter(Boolean).join(" · ");

  return (
    <EventLayout>
      <div className="max-w-5xl mx-auto px-5 sm:px-8 py-8 lg:py-12" data-testid="invitation-page">
        <p className="overline">Invitation</p>
        <h1 className="font-serif text-4xl tracking-tight">Customize your invitation</h1>
        <p className="text-stone-500 mt-3 max-w-2xl">Write the title and message your guests will see in their invitation email. Your event date, time and location are added automatically. Save &amp; preview as many times as you like before you send.</p>

        <div className="grid lg:grid-cols-2 gap-8 mt-8">
          <div className="space-y-5">
            <label className="block">
              <span className="text-xs font-medium text-stone-500 mb-1.5 block">Invitation title</span>
              <input value={form.custom_title} onChange={(e) => setForm((f) => ({ ...f, custom_title: e.target.value }))} data-testid="inv-title"
                placeholder={meta.event_title || "e.g. Sofia's 30th Birthday"}
                className="w-full rounded-md border border-[#E8E6E1] bg-white px-3.5 py-2.5 text-sm outline-none focus:border-[#8C9A86]" />
              <span className="text-[11px] text-stone-400 mt-1 block">Tell guests exactly who &amp; what the event is for. Defaults to "{meta.event_title}".</span>
            </label>
            <label className="block">
              <span className="text-xs font-medium text-stone-500 mb-1.5 block">Message / description</span>
              <textarea value={form.message} onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))} rows={6} data-testid="inv-message"
                placeholder="We'd be honoured to have you celebrate with us…"
                className="w-full rounded-md border border-[#E8E6E1] bg-white px-3.5 py-2.5 text-sm outline-none focus:border-[#8C9A86]" />
            </label>

            <div>
              <span className="text-xs font-medium text-stone-500 mb-1.5 block">Header photo (optional)</span>
              {meta.image_url ? (
                <div className="relative rounded-md overflow-hidden border border-[#E8E6E1]" data-testid="inv-image-preview">
                  <img src={meta.image_url} alt="Invitation header" className="w-full max-h-56 object-contain bg-stone-50" />
                  <button onClick={removeImage} data-testid="inv-image-remove" className="absolute top-2 right-2 rounded-full bg-white/90 text-[#C67A7A] p-1.5 hover:bg-white transition-colors"><Trash2 size={14} /></button>
                </div>
              ) : (
                <button onClick={() => imgRef.current?.click()} disabled={imgUploading} data-testid="inv-image-upload"
                  className="w-full rounded-md border border-dashed border-[#E8E6E1] py-6 text-sm text-stone-500 hover:border-[#8C9A86] transition-colors flex items-center justify-center gap-2 disabled:opacity-50">
                  <Upload size={15} /> {imgUploading ? "Uploading…" : "Upload a header photo"}
                </button>
              )}
              <input ref={imgRef} type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={uploadImage} data-testid="inv-image-input" />
              <span className="text-[11px] text-stone-400 mt-1 block">JPG, PNG or WEBP up to 10 MB. It appears at the top of the invitation email.</span>
            </div>

            <div className="rounded-md bg-[#F5F4F0] border border-[#E8E6E1] px-4 py-3 text-xs text-stone-500" data-testid="inv-auto-details">
              <span className="font-medium text-stone-700">Auto-included from your event:</span> {detailLine || "Add a date & location on the Event page to include them here."}
            </div>
            <button onClick={savePreview} disabled={saving} data-testid="inv-save-preview"
              className="flex items-center gap-2 rounded-full bg-[#2C2A28] text-white px-6 py-2.5 text-sm hover:bg-[#8C9A86] transition-colors disabled:opacity-50">
              <Eye size={15} /> {saving ? "Saving…" : "Save & preview"}
            </button>
          </div>

          <div>
            <span className="text-xs font-medium text-stone-500 mb-2 block">Live preview</span>
            {preview
              ? <iframe title="invitation preview" srcDoc={preview} data-testid="inv-preview" className="w-full h-[560px] rounded-lg border border-[#E8E6E1] bg-white" />
              : <div className="w-full h-[560px] rounded-lg border border-dashed border-[#E8E6E1] grid place-items-center text-sm text-stone-400" data-testid="inv-preview-empty">Click "Save &amp; preview" to see your invitation</div>}
          </div>
        </div>

        <div className="mt-14 border-t border-[#E8E6E1] pt-10" data-testid="rsvp-questions-section">
          <p className="overline">RSVP form</p>
          <h2 className="font-serif text-3xl tracking-tight">Choose your RSVP questions</h2>
          <p className="text-stone-500 mt-2 max-w-2xl">Every guest is always asked whether they'll attend. Turn on any extra questions you'd like to collect.</p>

          <div className="mt-6 grid sm:grid-cols-2 gap-3 max-w-3xl">
            <div className="rounded-lg border border-[#8C9A86]/40 bg-[#8C9A86]/8 px-4 py-3 flex items-start gap-3">
              <span className="mt-0.5 text-[#5f6b59]">✓</span>
              <div><p className="text-sm font-medium text-stone-800">Attendance</p><p className="text-xs text-stone-500">Always included.</p></div>
            </div>
            {QUESTIONS.map((qq) => (
              <label key={qq.key} data-testid={`rsvpq-${qq.key}`}
                className={`rounded-lg border px-4 py-3 flex items-start gap-3 cursor-pointer transition-colors ${cfg[qq.key] ? "border-[#8C9A86]/40 bg-[#8C9A86]/8" : "border-[#E8E6E1] bg-white hover:bg-stone-50"}`}>
                <input type="checkbox" checked={!!cfg[qq.key]} onChange={(e) => setCfg((c) => ({ ...c, [qq.key]: e.target.checked }))}
                  data-testid={`rsvpq-toggle-${qq.key}`} className="mt-1 accent-[#8C9A86]" />
                <div><p className="text-sm font-medium text-stone-800">{qq.label}</p><p className="text-xs text-stone-500">{qq.desc}</p></div>
              </label>
            ))}
          </div>

          {cfg.meal && (
            <label className="block mt-4 max-w-3xl">
              <span className="text-xs font-medium text-stone-500 mb-1.5 block">Meal options (comma separated)</span>
              <input value={mealText} onChange={(e) => setMealText(e.target.value)} data-testid="rsvpq-meal-options"
                placeholder="Chicken, Beef, Fish, Vegetarian, Vegan"
                className="w-full rounded-md border border-[#E8E6E1] bg-white px-3.5 py-2.5 text-sm outline-none focus:border-[#8C9A86]" />
            </label>
          )}

          <button onClick={saveCfg} disabled={savingCfg} data-testid="rsvpq-save"
            className="mt-5 rounded-full bg-[#2C2A28] text-white px-6 py-2.5 text-sm hover:bg-[#8C9A86] transition-colors disabled:opacity-50">
            {savingCfg ? "Saving…" : "Save RSVP questions"}
          </button>
        </div>

        <div className="mt-14 border-t border-[#E8E6E1] pt-10" data-testid="physical-invites-section">
          <div className="flex items-center gap-2"><QrCode size={18} className="text-[#8C9A86]" /><p className="overline">Physical invitations</p></div>
          <h2 className="font-serif text-3xl tracking-tight">A QR code for guests without email</h2>
          <p className="text-stone-500 mt-2 max-w-2xl">Add this code to printed invitations, place cards or a welcome sign. Guests scan it, find their name and RSVP, no account or email needed. Use printed and emailed invitations together, whenever suits each guest.</p>

          {invitesUnlocked ? (
            <div className="mt-6 grid sm:grid-cols-[auto,1fr] gap-6 items-start">
              <div className="bg-white border border-[#E8E6E1] rounded-lg p-4 w-fit">
                <img src={qrSrc} alt="RSVP QR code" width={200} height={200} data-testid="rsvp-qr-img" className="w-[200px] h-[200px]" />
              </div>
              <div className="space-y-4">
                <div>
                  <span className="text-xs font-medium text-stone-500 mb-1.5 block">Your RSVP-by-name link</span>
                  <div className="flex gap-2">
                    <input readOnly value={nameRsvpUrl} data-testid="name-rsvp-link" className="flex-1 rounded-md border border-[#E8E6E1] bg-stone-50 px-3.5 py-2.5 text-sm" />
                    <button onClick={() => { navigator.clipboard.writeText(nameRsvpUrl); toast.success("Link copied"); }} data-testid="copy-name-rsvp" className="flex items-center gap-1.5 rounded-full border border-[#E8E6E1] px-4 py-2 text-sm hover:bg-stone-50"><Copy size={14} /> Copy</button>
                  </div>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <button onClick={printQr} data-testid="print-qr-btn" className="flex items-center gap-2 rounded-full bg-[#2C2A28] text-white px-5 py-2.5 text-sm hover:bg-[#8C9A86] transition-colors"><Printer size={15} /> Print QR poster</button>
                  <a href={nameRsvpUrl} target="_blank" rel="noreferrer" data-testid="open-name-rsvp" className="flex items-center gap-2 rounded-full border border-[#E8E6E1] px-5 py-2.5 text-sm hover:bg-stone-50">Open RSVP page</a>
                </div>
              </div>
            </div>
          ) : (
            <div className="mt-6 rounded-lg border border-dashed border-[#E8E6E1] p-6 text-sm text-stone-500" data-testid="physical-locked">
              Unlock the Guest List &amp; Invitations bundle to generate your QR code and publish your RSVP-by-name page.
            </div>
          )}
        </div>
      </div>
    </EventLayout>
  );
}
